import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui";
import { AgentAvatar } from "@/workshop/components/canvas/agent-avatar";

/**
 * Workshop: Notification dropdown — compact popover from bell icon.
 *
 * Shows pending approvals, completed workflows, and errors.
 * Each item links to the relevant execution/task context.
 */

export type NotificationType = "approval" | "completed" | "failed" | "info";

export interface NotificationItem {
  id: string;
  type: NotificationType;
  agentLetter: string;
  agentColor: string;
  agentName: string;
  title: string;
  /** Optional short description (e.g. "Send email to client@…") */
  description?: string;
  timestamp: string;
  read: boolean;
}

interface NotificationDropdownProps {
  notifications: NotificationItem[];
  onClickItem?: (id: string) => void;
  onMarkAllRead?: () => void;
  onDismiss?: (id: string) => void;
  className?: string;
  "data-testid"?: string;
}

const TYPE_CONFIG: Record<
  NotificationType,
  { icon: React.ReactNode; accent: string }
> = {
  approval: {
    icon: <Icons.AlertTriangle className="h-3 w-3" />,
    accent: "text-warning",
  },
  completed: {
    icon: <Icons.Check className="h-3 w-3" />,
    accent: "text-emerald-400",
  },
  failed: {
    icon: <Icons.X className="h-3 w-3" />,
    accent: "text-red-400",
  },
  info: {
    icon: <Icons.Info className="h-3 w-3" />,
    accent: "text-blue-400",
  },
};

export function NotificationDropdown({
  notifications,
  onClickItem,
  onMarkAllRead,
  onDismiss,
  className,
  "data-testid": testId = "notification-dropdown",
}: NotificationDropdownProps) {
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div
      className={cn(
        "w-80 max-h-96 flex flex-col rounded-xl border border-glass-stroke bg-glass-fill-dense shadow-glass backdrop-blur-md",
        className,
      )}
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-glass-stroke-subtle px-3 py-2">
        <span className="text-[11px] font-semibold text-primary">
          Notifications
          {unreadCount > 0 && (
            <span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
              {unreadCount}
            </span>
          )}
        </span>
        {unreadCount > 0 && (
          <button
            type="button"
            className="text-[10px] text-accent transition-colors hover:text-accent/80"
            onClick={onMarkAllRead}
            data-testid="notification-mark-all-read"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center gap-1.5 py-8">
            <Icons.Bell className="h-5 w-5 text-muted" />
            <p className="text-[10px] text-muted">No notifications</p>
          </div>
        ) : (
          notifications.map((item) => {
            const config = TYPE_CONFIG[item.type];
            return (
              <div
                key={item.id}
                className={cn(
                  "group flex cursor-pointer gap-2.5 px-3 py-2.5 transition-colors hover:bg-glass-fill-heavy",
                  !item.read && "bg-accent/[0.04]",
                )}
                onClick={() => onClickItem?.(item.id)}
                data-testid={`notification-item-${item.id}`}
              >
                {/* Agent avatar */}
                <div className="mt-0.5 shrink-0">
                  <AgentAvatar
                    letter={item.agentLetter}
                    color={item.agentColor}
                    size="sm"
                    hideStatus
                  />
                </div>

                {/* Content */}
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className={cn("shrink-0", config.accent)}>
                      {config.icon}
                    </span>
                    <span className="truncate text-[10px] font-medium text-primary">
                      {item.title}
                    </span>
                    {!item.read && (
                      <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    )}
                  </div>
                  {item.description && (
                    <p className="truncate text-[9px] text-secondary">
                      {item.description}
                    </p>
                  )}
                  <div className="flex items-center gap-1">
                    <span className="text-[9px] text-muted">
                      {item.agentName}
                    </span>
                    <span className="text-[9px] text-muted">·</span>
                    <span className="text-[9px] text-muted">
                      {item.timestamp}
                    </span>
                  </div>
                </div>

                {/* Dismiss */}
                <button
                  type="button"
                  className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded opacity-0 transition-opacity hover:bg-glass-fill-heavy group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss?.(item.id);
                  }}
                  data-testid={`notification-dismiss-${item.id}`}
                  aria-label="Dismiss notification"
                >
                  <Icons.X className="h-2.5 w-2.5 text-muted" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Footer — view all approvals shortcut */}
      {notifications.some((n) => n.type === "approval" && !n.read) && (
        <div className="border-t border-glass-stroke-subtle px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-[10px] text-accent"
            data-testid="notification-view-approvals"
          >
            <Icons.Eye className="mr-1 h-3 w-3" />
            View pending approvals
          </Button>
        </div>
      )}
    </div>
  );
}

export type { NotificationDropdownProps };
